import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { handLineAfter } from '../handLine';

const WORD_START = 0.3;
const WORD_STEP = 0.04;
const WORD_DURATION = 0.9;
const FOG_DURATION = 2.2;
const HOME_PATHS = new Set(['/', '/work', '/recruiter']);

let playingThisLoad = false;
let introSeen = false;

function markIntroSeen() {
  introSeen = true;
}

function hasSeenIntro() {
  return introSeen;
}

function shouldPlayIntro() {
  if (playingThisLoad) return true;
  if (hasSeenIntro()) return false;
  playingThisLoad = true;
  return true;
}

if (!HOME_PATHS.has(window.location.pathname)) {
  markIntroSeen();
}

export function markIntroSeenOnLeave(entryPath, pathname) {
  const stayedOnHome = HOME_PATHS.has(entryPath) && HOME_PATHS.has(pathname);
  if (pathname !== entryPath && !stayedOnHome) {
    markIntroSeen();
    playingThisLoad = false;
  }
}

const wordReveal = keyframes`
  to {
    opacity: 1;
  }
`;

const Word = styled.span`
  display: inline-block;
  font-weight: ${(p) => (p.$strong ? 700 : 400)};
  color: ${(p) => (p.$strong ? '#313131' : 'inherit')};

  html[data-theme='dark'] & {
    color: ${(p) => (p.$strong ? '#d1d1d1' : 'inherit')};
  }
  opacity: ${(p) => (p.$play ? 0.15 : 1)};
  animation: ${(p) => (p.$play
    ? css`${wordReveal} ${WORD_DURATION}s cubic-bezier(0.23, 1, 0.32, 1) forwards`
    : 'none')};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const previewLinkStyles = `
  position: relative;
  color: inherit;
  text-decoration: none;

  &::after {
    ${handLineAfter}
  }

  &:hover {
    color: var(--text);
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: scaleY(1.2);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const PreviewRoute = styled(Link)`
  ${previewLinkStyles}
`;

const PreviewAnchor = styled.a`
  ${previewLinkStyles}
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const Card = styled.span`
  position: fixed;
  z-index: 40;
  width: min(320px, calc(100vw - 24px));
  padding: 10px;
  border-radius: 12px;
  background: var(--bg);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${cardIn} 0.16s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Shot = styled.img`
  width: 100%;
  height: 168px;
  object-fit: ${(p) => p.$fit || 'contain'};
  object-position: center;
  border-radius: 12px;
  background: ${(p) => p.$bg || '#111'};
  display: block;
`;

const Meta = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 6px 6px;
  text-align: left;
`;

const Icon = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  object-fit: cover;
  flex: none;
  background: var(--surface);
`;

const Title = styled.span`
  display: block;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--text);
  line-height: 1.2;
`;

const Sub = styled.span`
  display: block;
  margin-top: 2px;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--text-muted);
  line-height: 1.3;
`;

function placeCard(anchor, card) {
  const r = anchor.getBoundingClientRect();
  const w = card ? card.offsetWidth : 320;
  const h = card ? card.offsetHeight : 248;
  const pad = 12;
  let left = r.left + Math.min(16, r.width * 0.25);
  let top = r.top + r.height / 2 - h / 2;
  left = Math.min(Math.max(pad, left), window.innerWidth - w - pad);
  top = Math.min(Math.max(pad, top), window.innerHeight - h - pad);
  return { left, top };
}

function PreviewLink({ to, href, preview, children }) {
  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 12, top: 12 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return undefined;
    const update = () => {
      if (!anchorRef.current) return;
      setPos(placeCard(anchorRef.current, cardRef.current));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  const card = open && (
    <Card ref={cardRef} style={{ left: pos.left, top: pos.top }} aria-hidden="true">
      {preview.image && <Shot src={preview.image} alt="" $bg={preview.imageBg} $fit={preview.fit} />}
      <Meta>
        <Icon src={preview.icon} alt="" />
        <span>
          <Title>{preview.title}</Title>
          <Sub>{preview.subtitle}</Sub>
        </span>
      </Meta>
    </Card>
  );

  const handlers = {
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
  };

  if (to) {
    return (
      <PreviewRoute to={to} ref={anchorRef} {...handlers}>
        {children}
        {card}
      </PreviewRoute>
    );
  }

  return (
    <PreviewAnchor href={href} target="_blank" rel="noopener noreferrer" ref={anchorRef} {...handlers}>
      {children}
      {card}
    </PreviewAnchor>
  );
}

const BannerContainer = styled.section`
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px 128px;
  position: relative;
  background: var(--bg);
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 0 20px 128px;
  }
`;

const BannerContent = styled.div`
  max-width: 1120px;
  width: 100%;
  text-align: left;
  position: relative;
  z-index: 2;
  margin: 0 auto;
`;

const IntroWrap = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const NameLine = styled.span`
  display: block;
  margin-bottom: 8px;
  text-align: center;
`;

const IntroLead = styled.h1`
  margin: 0;
  max-width: 28em;
  font-family: var(--font-serif);
  font-size: clamp(1.15rem, 2.15vw, 1.45rem);
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.02em;
  text-align: center;
  color: #8a8a8a;

  html[data-theme='dark'] & {
    color: #a3a3a3;
  }

  @media (max-width: 640px) {
    font-size: 1.1rem;
    max-width: 22em;
  }
`;

function fillWords(text, counter, play, strong) {
  return text.split(/(\s+)/).map((part) => {
    if (!part) return null;
    if (!part.trim()) return part;
    const index = counter.i;
    counter.i += 1;
    return (
      <Word
        key={index}
        $play={play}
        $strong={strong}
        style={play ? { animationDelay: `${WORD_START + index * WORD_STEP}s` } : undefined}
      >
        {part}
      </Word>
    );
  });
}

const Banner = () => {
  const playIntro = useRef(shouldPlayIntro());
  const counter = useRef({ i: 0 });
  counter.current = { i: 0 };
  const words = (text, strong) => fillWords(text, counter.current, playIntro.current, strong);
  const lead = (
    <>
      <NameLine>{words('Sarah Jun', true)}</NameLine>
      {words('Product and conversation designer at')}{' '}
      <PreviewLink
        href="https://www.accenture.com/en/about/accenture-song-index"
        preview={{
          icon: '/logos/accenture-song.jpg',
          title: 'Accenture Song',
          subtitle: 'Product and conversation design',
        }}
      >
        {words('Accenture Song')}
      </PreviewLink>
      {words(', working in the gap between how things should work and how people actually use them. previously')}{' '}
      <PreviewLink
        to="/case-studies/wayfair-ai-assistant"
        preview={{
          image: '/wayfair-ai-assistant.png',
          fit: 'cover',
          icon: '/logos/wayfair.svg',
          title: 'Wayfair',
          subtitle: 'AI for 11k+ businesses',
        }}
      >
        {words('Wayfair')}
      </PreviewLink>
      {words('; cofounded')}{' '}
      <PreviewLink
        to="/case-studies/freebites"
        preview={{
          image: '/freebites.png',
          icon: '/logos/freebites.svg',
          title: 'Freebites',
          subtitle: 'Free campus food · 6k+ downloads',
        }}
      >
        {words('Freebites')}
      </PreviewLink>
      {words('.')}
    </>
  );
  const wordCount = counter.current.i;
  const fogDelay = WORD_START + Math.max(0, wordCount - 1) * WORD_STEP + WORD_DURATION;

  useLayoutEffect(() => {
    if (!playIntro.current) return undefined;
    markIntroSeen();
    let settled = false;
    const settle = window.setTimeout(() => {
      settled = true;
    }, 0);
    const release = () => {
      window.clearTimeout(settle);
      if (settled) playingThisLoad = false;
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return release;
    const root = document.body;
    root.style.setProperty('--intro-fog-delay', `${fogDelay}s`);
    root.classList.add('intro-writing');
    const timer = window.setTimeout(() => {
      root.classList.remove('intro-writing');
      root.style.removeProperty('--intro-fog-delay');
    }, (fogDelay + FOG_DURATION) * 1000 + 40);
    return () => {
      release();
      window.clearTimeout(timer);
      root.classList.remove('intro-writing');
      root.style.removeProperty('--intro-fog-delay');
    };
  }, [fogDelay]);

  return (
    <BannerContainer id="home">
      <BannerContent>
        <IntroWrap>
          <IntroLead>{lead}</IntroLead>
        </IntroWrap>
      </BannerContent>
    </BannerContainer>
  );
};

export default Banner;
